import React, { Component } from 'react';
import styled from 'styled-components';
import { push, replace } from 'modules/history';
import { Colors } from 'modules/theme';
import InputSearch from 'components/InputSearch';
import Filter, { FilterGroup, FilterContainer } from 'components/Filter';
import Table, { THeader, TableTitle, TRow, TableItem, TBody } from 'components/Table';
import PaginationButtons from 'components/PaginationButtons';
import Icon from 'components/Icon';
import Loader from 'components/Loader';
import { Tooltip } from 'react-tooltip';
import PropTypes from 'prop-types';
import moment from 'moment';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  background: ${Colors.white};
  padding: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  padding-bottom: 20px;
  gap: 16px;
  min-height: 44px;
  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const SearchWrapper = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 380px;
  display: flex;
  align-items: center;
  align-self: center;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    margin-right: 12px;
  }
`;

const CardsRow = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const Card = styled.div`
  flex: 1;
  min-width: 200px;
  padding: 20px;
  background-color: ${Colors.white};
  border: 1px solid #ecebf0;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;

  @media (max-width: 600px) {
    min-width: 100%;
  }
`;

const CardLoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 72px;
  flex: 1;
`;

const CardValue = styled.div`
  align-self: stretch;
  text-align: center;
  color: #314683;
  font-size: 20px;
  font-family: Gotham, 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 350;
  line-height: 28px;
  letter-spacing: 0.38px;
  word-wrap: break-word;
`;

const CardLabel = styled.div`
  align-self: stretch;
  text-align: center;
  color: #1c1c1c;
  font-size: 16px;
  font-family: Gotham, 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 325;
  line-height: 24px;
  word-wrap: break-word;
`;

const CardDetails = styled.div`
  align-self: stretch;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const CardDetail = styled.span`
  color: #1c1c1c;
  font-size: 12px;
  font-family: Gotham, 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 300;
  line-height: 16px;
  word-wrap: break-word;
`;

const FiltersRow = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 24px;
`;

const FiltersGroup = styled(FilterGroup)`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 20px 24px;
  padding: 20px;
  margin-top: 0;
  margin-bottom: 0;

  @media (max-width: 900px) {
    flex-wrap: wrap;
    gap: 16px;
    padding: 16px;
  }
`;

const FilterWrapper = styled(FilterContainer)`
  flex: 1 1 160px;
  min-width: 120px;
  max-width: 240px;
  margin-right: 0;

  @media (max-width: 900px) {
    flex: 1 1 100%;
    min-width: 0;
    max-width: 100%;
  }
`;

const LocationFilterWrapper = styled(FilterWrapper)`
  flex: 1 1 200px;
  max-width: 280px;

  @media (max-width: 900px) {
    max-width: 100%;
  }
`;

const TableTitleStyled = styled(TableTitle)`
  color: #808285;
  font-size: 14px;
  font-family: Gotham, sans-serif;
  font-weight: 400;
  line-height: 18px;
  word-wrap: break-word;
`;

const TableTitleWithInfo = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const InfoIconButton = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: ${Colors.gray8};
  cursor: help;
`;

const TableWrapper = styled.div`
  background: ${Colors.white};
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
`;

const StateBadge = styled.span`
  display: inline-flex;
  padding: 10px 20px;
  border-radius: 30px;
  font-size: 16px;
  font-family: Gotham, sans-serif;
  font-weight: 400;
  line-height: 24px;
  word-wrap: break-word;
  justify-content: center;
  align-items: center;
  gap: 10px;
  background: ${props => props.bg};
  color: ${props => props.color};
`;

const StatusIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 99px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${props => props.bg};
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
`;

const StatusIconsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
`;

const ViewDetailsLink = styled.span`
  color: ${props => (props.$disabled ? '#BDBFC1' : '#1498CB')};
  font-size: 14px;
  font-family: Gotham, 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 350;
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
  pointer-events: ${props => (props.$disabled ? 'none' : 'auto')};
  ${props => props.$disabled && 'text-decoration: none;'}
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-start;
  padding: 16px 0;
`;

const DEVICE_TYPE_LABELS = {
  'water-filter': 'Filter',
  'reverse-osmosis': 'Reverse Osmosis',
  'water-softener': 'Softener',
};

const STATUS_TO_STATE = {
  passed: 'Pass',
  failed: 'Failed',
  skipped: 'Skipped',
};

const STATE_STYLES = {
  Pass: { bg: '#DFF5E5', color: '#09AA36' },
  Failed: { bg: '#FBC7C7', color: '#C80000' },
  Skipped: { bg: '#E5E7EA', color: '#808285' },
};

const STATUS_COLORS = {
  green: { bg: '#DFF5E5', icon: '#09AA36' },
  red: { bg: '#FBC7C7', icon: '#CE1919' },
  gray: { bg: '#E5E7EA', icon: '#808285' },
};

const LOCATION_STATUS_ICONS = ['tint', 'wifi', 'bolt'];

const toLocationStatusColor = value => {
  if (value === true || value === 'passed') return 'green';
  if (value === false || value === 'failed') return 'red';
  return 'gray';
};

const toLocationStatusColors = (locationStatus = {}) => {
  const { water, internet, power } = locationStatus;
  return [
    toLocationStatusColor(water),
    toLocationStatusColor(internet),
    toLocationStatusColor(power),
  ];
};

function mapApiRowToView(row) {
  const date = row.completed_at || row.started_at;
  const status = row.status ?? row.overall_status;
  const state = STATUS_TO_STATE[status] || 'Skipped';
  return {
    report_id: row.report_id ?? row.id,
    location_id: row.location_id,
    device_type: row.device_type,
    location: row.location_name || row.location || '—',
    brand: row.brand_name || row.brand || '—',
    device: DEVICE_TYPE_LABELS[row.device_type] || row.device_type || '—',
    date: date ? moment(date).format('MM/DD/YYYY HH:mm:ss') : '—',
    attempts:
      row.attempts != null && row.attempts !== ''
        ? Number(row.attempts)
        : Math.max(0, Number(row.retry_count || 0)) + 1,
    state,
    locationStatus:
      state === 'Pass' ? ['green', 'green', 'green'] : toLocationStatusColors(row.location_status),
    // Skipped runs (e.g. installer skip after retries) still have report detail + attempts in the API.
    hasDetails: row.report_id != null && row.report_id !== '',
  };
}

const STATE_FILTER_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Pass', value: 'passed' },
  { label: 'Failed', value: 'failed' },
  { label: 'Skipped', value: 'skipped' },
];

export default class HealthChecksView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      search: '',
      brand_id: props.initialBrandId || '',
      location_id: props.initialLocationId || '',
      overall_status: props.initialOverallStatus || '',
    };
  }

  static propTypes = {
    brands: PropTypes.array.isRequired,
    fetchHealthChecks: PropTypes.func.isRequired,
    fetchLocationsForFilter: PropTypes.func.isRequired,
    initialBrandId: PropTypes.string,
    initialLocationId: PropTypes.string,
    initialOverallStatus: PropTypes.string,
    list: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,
    location: PropTypes.object,
    locations: PropTypes.array.isRequired,
    metrics: PropTypes.object,
    metricsLoading: PropTypes.bool.isRequired,
    pagination: PropTypes.object.isRequired,
  };

  static defaultProps = {
    initialBrandId: '',
    initialLocationId: '',
    initialOverallStatus: '',
  };

  componentDidMount() {
    const params = new URLSearchParams(this.props.location?.search || '');
    const search = params.get('search') || '';
    if (search !== this.state.search) {
      // Sync search from URL on initial load
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState({ search });
    }
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.initialBrandId !== this.props.initialBrandId ||
      prevProps.initialLocationId !== this.props.initialLocationId ||
      prevProps.initialOverallStatus !== this.props.initialOverallStatus
    ) {
      // Sync filter state from URL when query params change
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        brand_id: this.props.initialBrandId || '',
        location_id: this.props.initialLocationId || '',
        overall_status: this.props.initialOverallStatus || '',
      });
    }
    if (prevProps.location?.search !== this.props.location?.search) {
      const params = new URLSearchParams(this.props.location?.search || '');
      const search = params.get('search') || '';
      if (search !== this.state.search) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ search });
      }
    }
  }

  buildSearchParams = (override = {}) => {
    const { location, pagination } = this.props;
    const params = new URLSearchParams(location?.search || '');
    const limit = Number(params.get('limit')) || pagination?.limit || 10;
    const offset =
      override.offset !== undefined ? override.offset : Number(params.get('offset')) || 0;
    const next = new URLSearchParams();
    next.set('limit', limit);
    next.set('offset', offset);
    const brand_id = override.brand_id !== undefined ? override.brand_id : this.state.brand_id;
    const location_id =
      override.location_id !== undefined ? override.location_id : this.state.location_id;
    const overall_status =
      override.overall_status !== undefined ? override.overall_status : this.state.overall_status;
    const search =
      override.search !== undefined ? override.search : (this.state.search || '').trim();
    if (brand_id) next.set('brand_id', brand_id);
    if (location_id) next.set('location_id', location_id);
    if (overall_status) next.set('overall_status', overall_status);
    if (search) next.set('search', search);
    return next;
  };

  /** Build params object for API (same pattern as Location List: fetch directly on change) */
  getFetchParams = (override = {}) => {
    const next = this.buildSearchParams(override);
    const limit = Number(next.get('limit')) || 10;
    const offset = Number(next.get('offset')) || 0;
    const brand_id = next.get('brand_id') || undefined;
    const location_id = next.get('location_id') || undefined;
    const overall_status = next.get('overall_status') || undefined;
    const search = (next.get('search') || '').trim() || undefined;
    return { limit, offset, brand_id, location_id, overall_status, search };
  };

  filterOnChange = event => {
    const { name, value } = event.target;
    const { fetchLocationsForFilter, fetchHealthChecks } = this.props;
    this.setState({ [name]: value, ...(name === 'brand_id' ? { location_id: '' } : {}) }, () => {
      if (name === 'brand_id') {
        fetchLocationsForFilter(value ? { brands: [value] } : {});
      }
      const override = {
        brand_id: name === 'brand_id' ? value : this.state.brand_id,
        location_id:
          name === 'location_id' ? value : name === 'brand_id' ? '' : this.state.location_id,
        overall_status: name === 'overall_status' ? value : this.state.overall_status,
        offset: 0,
      };
      const next = this.buildSearchParams(override);
      replace(`/health-checks?${next.toString()}`);
      const params = this.getFetchParams(override);
      fetchHealthChecks(params);
    });
  };

  searchOnChange = search => {
    this.setState({ search }, () => {
      const { fetchHealthChecks } = this.props;
      const override = { search, offset: 0 };
      const next = this.buildSearchParams(override);
      replace(`/health-checks?${next.toString()}`);
      const params = this.getFetchParams(override);
      fetchHealthChecks(params);
    });
  };

  changePage = page => {
    const limit = this.props.pagination?.limit || 10;
    const offset = (page - 1) * limit;
    const next = this.buildSearchParams({ offset });
    replace(`/health-checks?${next.toString()}`);
    const params = this.getFetchParams({ offset });
    this.props.fetchHealthChecks(params);
  };

  goToDetail = row => {
    if (row.report_id == null || row.report_id === '') return;
    push(`/health-checks/detail/${row.report_id}`);
  };

  renderLocationStatus = statuses => (
    <StatusIconsRow>
      {statuses.map((status, idx) => {
        const style = STATUS_COLORS[status] || STATUS_COLORS.gray;
        return (
          <StatusIcon key={LOCATION_STATUS_ICONS[idx]} bg={style.bg}>
            <Icon name={LOCATION_STATUS_ICONS[idx]} fontSize={14} color={style.icon} />
          </StatusIcon>
        );
      })}
    </StatusIconsRow>
  );

  render() {
    const { list, metrics, loading, metricsLoading, pagination, brands, locations } = this.props;
    const { brand_id, location_id, overall_status } = this.state;

    const limit = pagination.limit || 10;
    const offset = pagination.offset || 0;
    const currentPage = limit ? Math.floor(offset / limit) + 1 : 1;
    const hasMore = list.length === limit;
    const totalPages = hasMore ? currentPage + 1 : currentPage;

    const successRate = metrics?.success_rate || {};
    const skipRate = metrics?.skip_rate || {};
    const retryRate = metrics?.retry_rate || {};

    const rows = list.map(mapApiRowToView);

    return (
      <Container>
        <Header>
          <SearchWrapper>
            <InputSearch onChange={this.searchOnChange} value={this.state.search} name="search" />
          </SearchWrapper>
        </Header>

        <CardsRow>
          <Card>
            <CardLabel>Success rate</CardLabel>
            {metricsLoading ? (
              <CardLoadingWrap>
                <Loader size={28} />
              </CardLoadingWrap>
            ) : (
              <>
                <CardValue>{successRate.rate != null ? successRate.rate : '—'}%</CardValue>
                <CardDetails>
                  <CardDetail>Performed: {successRate.performed ?? '—'}</CardDetail>
                  <CardDetail>Failed: {successRate.failed ?? '—'}</CardDetail>
                  <CardDetail>Succeed: {successRate.succeeded ?? '—'}</CardDetail>
                </CardDetails>
              </>
            )}
          </Card>
          <Card>
            <CardLabel>Skip rate</CardLabel>
            {metricsLoading ? (
              <CardLoadingWrap>
                <Loader size={28} />
              </CardLoadingWrap>
            ) : (
              <>
                <CardValue>{skipRate.rate != null ? skipRate.rate : '—'}%</CardValue>
                <CardDetails>
                  <CardDetail>Installed: {skipRate.installed ?? '—'}</CardDetail>
                  <CardDetail>Skipped: {skipRate.skipped ?? '—'}</CardDetail>
                </CardDetails>
              </>
            )}
          </Card>
          <Card>
            <CardLabel>Retry rate</CardLabel>
            {metricsLoading ? (
              <CardLoadingWrap>
                <Loader size={28} />
              </CardLoadingWrap>
            ) : (
              <>
                <CardValue>{retryRate.rate != null ? retryRate.rate : '—'}%</CardValue>
                <CardDetails>
                  <CardDetail>Performed: {retryRate.performed ?? '—'}</CardDetail>
                  <CardDetail>Retry: {retryRate.retry ?? '—'}</CardDetail>
                </CardDetails>
              </>
            )}
          </Card>
        </CardsRow>

        <FiltersRow>
          <FiltersGroup>
            <FilterWrapper>
              <Filter
                title="Brand:"
                selectedValue={brand_id}
                name="brand_id"
                handleChange={this.filterOnChange}
                options={brands}
              />
            </FilterWrapper>
            <LocationFilterWrapper>
              <Filter
                title="Locations:"
                selectedValue={location_id}
                name="location_id"
                handleChange={this.filterOnChange}
                options={locations}
              />
            </LocationFilterWrapper>
            <FilterWrapper>
              <Filter
                title="State:"
                selectedValue={overall_status}
                name="overall_status"
                handleChange={this.filterOnChange}
                options={STATE_FILTER_OPTIONS}
              />
            </FilterWrapper>
          </FiltersGroup>
        </FiltersRow>

        <TableWrapper>
          <Table>
            <THeader minWidth="1180px">
              <TableTitleStyled width="14%">Location</TableTitleStyled>
              <TableTitleStyled width="12%">Brand</TableTitleStyled>
              <TableTitleStyled width="12%">Device</TableTitleStyled>
              <TableTitleStyled width="15%">Date</TableTitleStyled>
              <TableTitleStyled width="7%">Attempts</TableTitleStyled>
              <TableTitleStyled width="11%">State</TableTitleStyled>
              <TableTitleStyled width="16%">
                <TableTitleWithInfo>
                  Location Status
                  <InfoIconButton
                    data-tooltip-id="location-status-column-tooltip"
                    data-tooltip-content="Note: This column indicates the location status for Water, internet, and power at the time of the health check. It does not indicate the current status of the location."
                  >
                    <Icon name="info-circle" fontSize={14} color={Colors.gray8} />
                  </InfoIconButton>
                </TableTitleWithInfo>
                <Tooltip id="location-status-column-tooltip" place="top" />
              </TableTitleStyled>
              <TableTitleStyled width="13%">More...</TableTitleStyled>
            </THeader>
            <TBody>
              {loading ? (
                <TRow>
                  <TableItem
                    width="100%"
                    style={{ justifyContent: 'center', padding: '48px 16px' }}
                  >
                    <Loader size={48} block />
                  </TableItem>
                </TRow>
              ) : rows.length === 0 ? (
                <TRow>
                  <TableItem
                    width="100%"
                    style={{ justifyContent: 'center', padding: '48px 16px', color: '#808285' }}
                  >
                    No health checks found
                  </TableItem>
                </TRow>
              ) : (
                rows.map((row, key) => {
                  const stateStyle = STATE_STYLES[row.state] || STATE_STYLES.Skipped;
                  return (
                    <TRow key={row.report_id || key} minWidth="1180px">
                      <TableItem width="14%">{row.location}</TableItem>
                      <TableItem width="12%">{row.brand}</TableItem>
                      <TableItem width="12%">{row.device}</TableItem>
                      <TableItem width="15%">{row.date}</TableItem>
                      <TableItem width="7%">{row.attempts}</TableItem>
                      <TableItem width="11%" isComponent style={{ justifyContent: 'center' }}>
                        <StateBadge bg={stateStyle.bg} color={stateStyle.color}>
                          {row.state}
                        </StateBadge>
                      </TableItem>
                      <TableItem width="16%" isComponent style={{ justifyContent: 'center' }}>
                        {this.renderLocationStatus(row.locationStatus)}
                      </TableItem>
                      <TableItem width="13%" isComponent>
                        <ViewDetailsLink
                          $disabled={!row.hasDetails}
                          onClick={row.hasDetails ? () => this.goToDetail(row) : undefined}
                          role="button"
                          aria-disabled={!row.hasDetails}
                        >
                          View details
                        </ViewDetailsLink>
                      </TableItem>
                    </TRow>
                  );
                })
              )}
            </TBody>
          </Table>
        </TableWrapper>

        <Footer>
          <PaginationButtons
            currentPage={currentPage}
            changePage={this.changePage}
            totalPages={totalPages}
          />
        </Footer>
      </Container>
    );
  }
}
